/**
 * MiseEnPagePanel — sliding panel for layout/template options in simple mode.
 *
 * Same content as VisualPanel but without the Export section, since Export
 * is accessible via the dedicated "Exporter" button in the action rail.
 */

import { Layout, Palette, Sparkles } from 'lucide-react';
import type { getTType } from '../../contexts/I18nContext';
import type { LayoutTemplate, PreviewEngine } from '../../hooks/useEditorUi';
import type { MarginSize } from '../panels/VisualPanel';

export interface MiseEnPagePanelProps {
  t: getTType;
  isMiseEnPageOpen: boolean;
  layoutTemplate: LayoutTemplate;
  setLayoutTemplate: (next: LayoutTemplate) => void;
  previewEngine: PreviewEngine;
  setPreviewEngine: (next: PreviewEngine) => void;
  maxPages: number | null;
  setMaxPages: (next: number | null) => void;
  marginSize: MarginSize;
  setMarginSize: (next: MarginSize) => void;
}

export function MiseEnPagePanel({
  t,
  isMiseEnPageOpen,
  layoutTemplate,
  setLayoutTemplate,
  previewEngine,
  setPreviewEngine,
  maxPages,
  setMaxPages,
  marginSize,
  setMarginSize,
}: MiseEnPagePanelProps) {
  return (
    <div
      className={`shrink-0 transition-all duration-300 ease-in-out overflow-hidden ${
        isMiseEnPageOpen ? 'w-[260px]' : 'w-0'
      }`}
    >
      <div className="h-full border-l border-white/10 bg-white/[0.01] overflow-y-auto scrollbar-hide">
        <div className="p-4 space-y-5">
          {/* Header */}
          <div className="pt-2">
            <h2 className="flex items-center gap-2 text-base font-black tracking-tight text-white">
              <div className="w-7 h-7 rounded-lg bg-purple-600/20 flex items-center justify-center text-purple-400">
                <Palette size={16} />
              </div>
              Mise en page
            </h2>
          </div>

          {/* Template */}
          <div className="space-y-3 border-t border-white/10 pt-4">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-white">
              <Layout size={16} className="text-purple-400" />
              Template
            </h3>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => setLayoutTemplate('ats')}
                className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-all ${
                  layoutTemplate === 'ats'
                    ? 'bg-green-600 text-white'
                    : 'text-white/40 hover:text-white/60 bg-white/5'
                }`}
              >
                ATS
              </button>
              <button
                type="button"
                onClick={() => setLayoutTemplate('moderne')}
                className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-all ${
                  layoutTemplate === 'moderne'
                    ? 'bg-blue-600 text-white'
                    : 'text-white/40 hover:text-white/60 bg-white/5'
                }`}
              >
                Moderne
              </button>
              <button
                type="button"
                onClick={() => setLayoutTemplate('classique')}
                className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-all ${
                  layoutTemplate === 'classique'
                    ? 'bg-blue-600 text-white'
                    : 'text-white/40 hover:text-white/60 bg-white/5'
                }`}
              >
                Classique
              </button>
              <button
                type="button"
                onClick={() => setLayoutTemplate('pro')}
                className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-all ${
                  layoutTemplate === 'pro'
                    ? 'bg-purple-600 text-white'
                    : 'text-white/40 hover:text-white/60 bg-white/5'
                }`}
              >
                Pro
              </button>
            </div>
          </div>

          {/* Aperçu */}
          <div className="space-y-3 border-t border-white/10 pt-4">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-white">
              <Sparkles size={16} className="text-purple-400" />
              Aperçu
            </h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-white/50 uppercase font-bold block mb-1">
                  Moteur
                </label>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => setPreviewEngine('react')}
                    className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-all ${
                      previewEngine === 'react'
                        ? 'bg-amber-600 text-white'
                        : 'text-white/40 hover:text-white/60 bg-white/5'
                    }`}
                  >
                    React
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewEngine('typst')}
                    className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-all ${
                      previewEngine === 'typst'
                        ? 'bg-amber-600 text-white'
                        : 'text-white/40 hover:text-white/60 bg-white/5'
                    }`}
                  >
                    Typst
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs text-white/50 uppercase font-bold block mb-1">
                  Pages
                </label>
                <select
                  value={maxPages || 'all'}
                  onChange={(e) =>
                    setMaxPages(e.target.value === 'all' ? null : parseInt(e.target.value))
                  }
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white"
                >
                  <option value="all">Toutes</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5">5</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-white/50 uppercase font-bold block mb-1">Marges</label>
                <select
                  value={marginSize}
                  onChange={(e) => setMarginSize(e.target.value as MarginSize)}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white"
                >
                  <option value="none">Aucune</option>
                  <option value="small">{t('visuel.small')}</option>
                  <option value="medium">Moyennes</option>
                  <option value="large">Grandes</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
